import * as React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Armchair,
  Blocks,
  FilePlus,
  Loader2,
  Mail,
  MailX,
  Palette,
  Pencil,
  ShieldCheck,
  Workflow,
} from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@eduviaos/ui-kit";
import type { ApiError } from "@eduviaos/shared";
import { api } from "@/lib/api";
import { AdmissionsPanel } from "@/components/admissions/AdmissionsPanel";
import { BrandingPanel } from "@/components/branding/BrandingPanel";
import { SeatingConfigPanel } from "@/components/seating/SeatingConfigPanel";
import { SeatSlotsCard } from "@/components/seating/SeatSlotsCard";
import { LearnerAppConfigPanel } from "@/components/learnerApp/LearnerAppConfigPanel";
import { LearnerFormPanel } from "@/components/learnerForm/LearnerFormPanel";
import { TeamFormPanel } from "@/components/teamForm/TeamFormPanel";
import { NewFormDialog } from "@/components/NewFormDialog";
import { SendAdminInviteDialog } from "@/components/SendAdminInviteDialog";
import { OpenCentreAdminButton } from "@/components/OpenCentreAdminDialog";
import { TenantDetailBreadcrumbs } from "@/components/AppBreadcrumbs";
import {
  CATEGORY_LABEL,
  CATEGORY_ORDER,
  isAvailable,
  type ModuleSpec,
  useModuleCatalog,
  type Vertical,
} from "@/lib/modules/catalog";

/**
 * Tenant Detail page — Phase B2.1.
 *
 * Three concerns:
 *   1. Overview — name, slug, type, status, created_at
 *   2. Settings (feature flags) — toggle `forms_editable_by_tenant` via
 *      `PATCH /super-admin/tenants/{slug}/feature-flags`
 *   3. Forms — read-only list of cloned forms via
 *      `GET /super-admin/tenants/{slug}/forms`, plus a "Clone more
 *      templates" entry-point that re-uses the wizard fieldset.
 *
 * The page deliberately mirrors the language used in the SA invite
 * wizard so the SA / account manager doesn't relearn vocabulary
 * mid-flow.
 */

interface TenantFeatureFlagsView {
  forms_editable_by_tenant: boolean;
  /** Phase B2.3 — enabled module keys. The BE serialises a Python
   *  set as a list. Default behaviour (back-compat with B1/B2 docs):
   *  if the field is missing OR null, treat as "all modules". */
  enabled_modules?: string[] | null;
  /** Phase B2.5b — cross-vertical override flag. When true, the SA
   *  can enable any catalog module regardless of the tenant's vertical.
   *  Default false. */
  module_override_vertical_matrix?: boolean;
  /** Phase H5d — when true the tenant admin (role == "admin") can
   *  author assessment templates, topics and questions in their own
   *  portal. When false the IA assessment write UI is hidden and the
   *  BE returns 403 FEATURE_FLAG_DISABLED. Reads are always allowed.
   *  Defaults to true for back-compat. */
  assessments_authoring_by_tenant?: boolean;
  /** Phase SC-22 — when true the tenant admin edits its own learner
   *  form; otherwise the IA settings page is read-only. Default false. */
  learner_form_editable_by_tenant?: boolean;
  /** Phase SC-22 — when true the tenant admin edits its own team form. */
  team_form_editable_by_tenant?: boolean;
}

interface TenantDetail {
  slug: string;
  name: string;
  type: "school" | "coaching_institute" | "study_centre";
  status: "active" | "suspended" | "archived";
  created_at: string;
  feature_flags: TenantFeatureFlagsView;
  /** Phase B2.3 — surfaces the deferred-invite affordance. */
  has_admin_user?: boolean;
}

interface TenantListResponse {
  items: TenantDetail[];
  total: number;
}

interface FormSummary {
  id: string;
  slug: string;
  name: string;
  purpose: string;
  version: number;
  is_active_version: boolean;
  fields: Array<{ key: string }>;
  requires_auth: boolean;
  updated_at: string;
}

interface FormListResponse {
  items: FormSummary[];
  total: number;
}

interface FormTemplateSummary {
  key: string;
  name: string;
  slug: string;
  description: string;
  field_count: number;
  requires_auth: boolean;
}

interface FormTemplateListResponse {
  vertical: string;
  items: FormTemplateSummary[];
}

interface CloneTemplatesResponse {
  cloned: string[];
  skipped: string[];
  invalid_keys: string[];
}

const TYPE_LABEL: Record<TenantDetail["type"], string> = {
  school: "School",
  coaching_institute: "Coaching Institute",
  study_centre: "Study Centre",
};

const STATUS_VARIANT: Record<TenantDetail["status"], "success" | "warn" | "danger"> = {
  active: "success",
  suspended: "warn",
  archived: "danger",
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

type DetailTab = "settings" | "forms" | "admissions" | "branding" | "seating";

const VALID_TABS: readonly DetailTab[] = ["settings", "forms", "admissions", "branding", "seating"];

function isValidTab(value: string | null): value is DetailTab {
  return value !== null && (VALID_TABS as readonly string[]).includes(value);
}

export function TenantDetailPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const qc = useQueryClient();

  // Honour `?tab=...` so the legacy `/tenants/:slug/branding` redirect
  // lands on the branding tab. Default to settings.
  const initialTab: DetailTab = isValidTab(searchParams.get("tab"))
    ? (searchParams.get("tab") as DetailTab)
    : "settings";
  const [activeTab, setActiveTab] = React.useState<DetailTab>(initialTab);

  const onTabChange = (next: string) => {
    if (!isValidTab(next)) return;
    setActiveTab(next);
    // Reflect the active tab in the URL so refresh + share work.
    const sp = new URLSearchParams(searchParams);
    if (next === "settings") {
      sp.delete("tab");
    } else {
      sp.set("tab", next);
    }
    setSearchParams(sp, { replace: true });
  };

  // Hydrate the detail object from the cached `tenants` list — avoids a
  // separate GET round-trip. Falls back to the network if cache is cold.
  const tenantsQuery = useQuery({
    queryKey: ["super-admin", "tenants"],
    queryFn: () => api.get<TenantListResponse>("/super-admin/tenants"),
  });
  const tenant = tenantsQuery.data?.items.find((t) => t.slug === slug) ?? null;

  // Phase E1c — flash-404 guard. We treat "tenant not found" as the result
  // of a *settled* query, not a *pending* one. `isLoading` only covers the
  // first-load case; if the SA navigates here right after creating a tenant
  // (TenantsPage seeds the cache then invalidates it), there's a refetch in
  // flight where `data` exists but the new slug hasn't landed yet. Without
  // the `isFetching && !tenant` arm, the user sees a 300 ms flash of
  // "Tenant not found" before the page snaps back to the real view.
  const isResolvingTenant =
    (tenantsQuery.isLoading && !tenant) || (tenantsQuery.isFetching && !tenant);

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-6">
      {isResolvingTenant ? (
        <div className="text-muted-foreground inline-flex items-center gap-2 text-sm">
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          Loading tenant…
        </div>
      ) : !tenant ? (
        <Card>
          <CardHeader>
            <CardTitle>Tenant not found</CardTitle>
            <CardDescription>
              No tenant with slug <code>{slug}</code> exists, or it was archived.
              <Link to="/tenants" className="ml-2 underline">
                Back to list
              </Link>
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <>
          {/*
            Default sticky=true: this page has long-form content
            (banner + tabs + cards) so keeping the breadcrumb +
            title pinned to the top while user scrolls adds value.
            The previous overlap with the awaiting-invite banner
            was caused by `-mt-8 mb-6` in PageHeader itself; that
            has been removed so sticky composes cleanly now.
          */}
          <PageHeader>
            <PageHeaderTop>
              <TenantDetailBreadcrumbs tenantName={tenant.name} />
              <PageHeaderActions>
                <Badge variant={STATUS_VARIANT[tenant.status]}>{tenant.status}</Badge>
                <OpenCentreAdminButton tenantSlug={tenant.slug} tenantName={tenant.name} />
              </PageHeaderActions>
            </PageHeaderTop>
            <PageHeaderHeading>
              <PageHeaderTitle>{tenant.name}</PageHeaderTitle>
              <PageHeaderDescription>
                <code className="bg-muted rounded px-1.5 py-0.5 text-[12px]">{tenant.slug}</code>
                {" · "}
                {TYPE_LABEL[tenant.type]}
                {" · "}
                created {formatDate(tenant.created_at)}
              </PageHeaderDescription>
            </PageHeaderHeading>
          </PageHeader>

          {/* Phase B2.3 — deferred-invite CTA. The banner unmounts the
              moment the BE confirms the invite (has_admin_user → true),
              so the SendAdminInviteDialog MUST live at the page level —
              otherwise it unmounts mid-success and the SA loses the
              one-shot accept URL it's still trying to display. */}
          <DeferredInviteSection tenant={tenant} />

          {/* Phase F-finale-7 Slice 7b.3 — the Seating tab is study-centre
              only (seat assignment is a study-centre capability). A stale
              `?tab=seating` on a school / coaching tenant falls back to
              settings so the tabs never render an empty body. */}
          {(() => {
            const isStudyCentre = tenant.type === "study_centre";
            const effectiveTab: DetailTab =
              activeTab === "seating" && !isStudyCentre ? "settings" : activeTab;
            return (
              <Tabs
                value={effectiveTab}
                onValueChange={onTabChange}
                className="grid grid-cols-[minmax(0,1fr)] gap-4"
              >
                <TabsList className="max-w-full justify-start overflow-x-auto">
                  <TabsTrigger value="settings">
                    <ShieldCheck className="size-3.5" aria-hidden="true" />
                    Settings
                  </TabsTrigger>
                  <TabsTrigger value="forms">
                    <FilePlus className="size-3.5" aria-hidden="true" />
                    Forms
                  </TabsTrigger>
                  <TabsTrigger value="admissions">
                    <Workflow className="size-3.5" aria-hidden="true" />
                    Admissions
                  </TabsTrigger>
                  <TabsTrigger value="branding">
                    <Palette className="size-3.5" aria-hidden="true" />
                    Branding
                  </TabsTrigger>
                  {isStudyCentre && (
                    <TabsTrigger value="seating">
                      <Armchair className="size-3.5" aria-hidden="true" />
                      Seating
                    </TabsTrigger>
                  )}
                </TabsList>

                <TabsContent value="settings" className="grid gap-4">
                  <FeatureFlagsCard tenant={tenant} qc={qc} />
                  {isStudyCentre ? <LearnerAppConfigPanel tenantSlug={tenant.slug} /> : null}
                  <ModulesCard tenant={tenant} qc={qc} />
                </TabsContent>

                <TabsContent value="forms" className="grid gap-4">
                  <LearnerFormPanel tenantSlug={tenant.slug} />
                  <TeamFormPanel tenantSlug={tenant.slug} />
                  <FormsCard tenant={tenant} qc={qc} />
                </TabsContent>

                <TabsContent value="admissions" className="grid gap-4">
                  <AdmissionsPanel
                    tenantSlug={tenant.slug}
                    enabledModules={tenant.feature_flags.enabled_modules ?? []}
                  />
                </TabsContent>

                <TabsContent value="branding" className="grid gap-4">
                  <BrandingPanel slug={tenant.slug} />
                </TabsContent>

                {isStudyCentre && (
                  <TabsContent value="seating" className="grid gap-4">
                    <SeatingConfigPanel tenantSlug={tenant.slug} />
                    <SeatSlotsCard tenantSlug={tenant.slug} />
                  </TabsContent>
                )}
              </Tabs>
            );
          })()}
        </>
      )}
    </div>
  );
}

// ── Deferred-invite section (Phase B2.3) ─────────────────────────

/**
 * Renders the amber "Awaiting admin invite" banner above the tabs and
 * owns the lifecycle of `<SendAdminInviteDialog>`.
 *
 * Critically: the Dialog is rendered UNCONDITIONALLY here. The banner
 * itself disappears once the BE confirms the invite (has_admin_user
 * flips true via cache invalidation), but the Dialog stays mounted —
 * so the success body with the one-shot accept URL keeps rendering
 * until the SA explicitly closes the Dialog. Without this split, the
 * SA was losing the URL the second the BE returned 201.
 */
function DeferredInviteSection({ tenant }: { tenant: TenantDetail }) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      {tenant.has_admin_user === false && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-300/70 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200">
          <div className="flex items-start gap-2.5">
            <MailX className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
            <div className="grid gap-0.5">
              <p className="text-sm font-semibold">Awaiting admin invite</p>
              <p className="text-xs opacity-90">
                This tenant doesn't have an admin user yet. Configure modules / forms / branding
                below, then send the first admin invite.
              </p>
            </div>
          </div>
          <Button onClick={() => setOpen(true)}>
            <Mail className="size-4" aria-hidden="true" />
            Send admin invite
          </Button>
        </div>
      )}
      {/* MUST render unconditionally — see component docstring. */}
      <SendAdminInviteDialog
        open={open}
        onOpenChange={setOpen}
        tenantSlug={tenant.slug}
        tenantName={tenant.name}
      />
    </>
  );
}

// ── Modules card (Phase B2.5c — API-driven, vertical-aware) ────

interface PatchFeatureFlagsBody {
  enabled_modules?: string[];
  module_override_vertical_matrix?: boolean;
  forms_editable_by_tenant?: boolean;
  assessments_authoring_by_tenant?: boolean;
  learner_form_editable_by_tenant?: boolean;
  team_form_editable_by_tenant?: boolean;
}

function ModulesCard({
  tenant,
  qc,
}: {
  tenant: TenantDetail;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const catalogQuery = useModuleCatalog();
  const catalog = catalogQuery.data;
  const vertical = tenant.type as Vertical;
  const overrideEnabled = tenant.feature_flags.module_override_vertical_matrix ?? false;

  // Pending confirm-modal state for cross-vertical enable. Holds the
  // module the SA wants to enable while we ask them to confirm flipping
  // the override. Null = no pending confirm.
  const [pendingCrossVertical, setPendingCrossVertical] = React.useState<ModuleSpec | null>(null);

  // null/undefined enabled_modules from the BE on a fresh tenant means
  // "still using BE defaults" — mirror that as the vertical's default
  // set so toggles render correctly the very first time. After the
  // first PATCH the BE always returns a concrete list.
  const enabled = React.useMemo<Set<string>>(() => {
    if (tenant.feature_flags.enabled_modules) {
      return new Set(tenant.feature_flags.enabled_modules);
    }
    if (!catalog) return new Set();
    const s = new Set<string>();
    for (const spec of catalog.raw.modules) {
      if (spec.default_for.includes(vertical)) s.add(spec.key);
    }
    return s;
  }, [tenant.feature_flags.enabled_modules, catalog, vertical]);

  const [error, setError] = React.useState<string | null>(null);

  const flagsMutation = useMutation<TenantDetail, ApiError, PatchFeatureFlagsBody>({
    mutationFn: (body) =>
      api.patch<TenantDetail>(`/super-admin/tenants/${tenant.slug}/feature-flags`, body),
    onSuccess: () => {
      setError(null);
      // Invalidate so the list page + any breadcrumbs pick up new state.
      qc.invalidateQueries({ queryKey: ["super-admin", "tenants"] });
    },
    onError: (e) => {
      // The BE returns 422 with code MODULE_NOT_AVAILABLE_FOR_VERTICAL
      // when the SA tries to enable a cross-vertical module without the
      // override. We map this to a plain-English message because the
      // happier paths (enabled-via-confirm-modal, enabled-while-override-on)
      // never trigger it.
      if (e.code === "MODULE_NOT_AVAILABLE_FOR_VERTICAL") {
        setError(
          "That module isn't available for this tenant's vertical. Enable the cross-vertical override above to allow it.",
        );
      } else {
        setError(e.message ?? "Failed to update modules.");
      }
    },
  });

  const persist = (next: Set<string>, extra?: Partial<PatchFeatureFlagsBody>) => {
    flagsMutation.mutate({
      enabled_modules: Array.from(next),
      ...extra,
    });
  };

  const toggleModule = (spec: ModuleSpec, currentlyChecked: boolean) => {
    setError(null);
    const next = new Set(enabled);
    const turningOn = !currentlyChecked;

    // Cross-vertical enable WITHOUT override → ask for confirmation
    // before flipping the override. Disable, or enable when override
    // is already on, falls through to the default path.
    if (turningOn && !overrideEnabled && !isAvailable(spec, vertical, false)) {
      setPendingCrossVertical(spec);
      return;
    }

    if (turningOn) next.add(spec.key);
    else next.delete(spec.key);
    persist(next);
  };

  const confirmCrossVerticalEnable = () => {
    if (!pendingCrossVertical) return;
    const next = new Set(enabled);
    next.add(pendingCrossVertical.key);
    persist(next, { module_override_vertical_matrix: true });
    setPendingCrossVertical(null);
  };

  const toggleOverride = (nextValue: boolean) => {
    setError(null);
    flagsMutation.mutate({ module_override_vertical_matrix: nextValue });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Blocks className="text-muted-foreground size-4" aria-hidden="true" />
          <CardTitle>Modules</CardTitle>
          {catalog && (
            <span className="text-muted-foreground ml-auto text-xs">
              {enabled.size} of {catalog.raw.count} enabled
            </span>
          )}
        </div>
        <CardDescription>
          User-facing feature areas the tenant admin can use. Disabled modules are hidden from the
          IA portal sidebar; their APIs return 403 to non-platform users. Changes save immediately.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {/* Cross-vertical override row */}
        <div className="border-border/60 bg-muted/30 flex items-start justify-between gap-3 rounded-md border px-3 py-2.5">
          <div className="grid gap-0.5">
            <span className="text-sm font-medium">Allow cross-vertical modules</span>
            <span className="text-muted-foreground text-xs">
              Off by default. When ON, modules outside this tenant's vertical (
              <code className="text-foreground/80">{vertical.replace("_", " ")}</code>) become
              selectable below. Use this only for genuine edge cases — most tenants stay within
              their vertical's catalog.
            </span>
          </div>
          <Switch
            checked={overrideEnabled}
            onCheckedChange={toggleOverride}
            disabled={flagsMutation.isPending}
            aria-label="Allow cross-vertical modules"
          />
        </div>

        {catalogQuery.isLoading ? (
          <p className="text-muted-foreground inline-flex items-center gap-1.5 py-2 text-xs">
            <Loader2 className="size-3 animate-spin" aria-hidden="true" />
            Loading module catalog…
          </p>
        ) : catalogQuery.isError || !catalog ? (
          <p className="text-destructive py-2 text-xs">
            Failed to load module catalog. Refresh the page to retry.
          </p>
        ) : (
          CATEGORY_ORDER.map((cat) => {
            const specs = catalog.byCategory[cat];
            // Hide categories that have zero modules at all (defensive
            // — the BE catalog shouldn't ever produce this, but it
            // makes empty-state behaviour graceful).
            if (specs.length === 0) return null;
            return (
              <div key={cat} className="grid gap-2">
                <p className="text-muted-foreground text-[11px] font-medium uppercase tracking-wide">
                  {CATEGORY_LABEL[cat]}
                </p>
                <ul className="grid gap-2 sm:grid-cols-2">
                  {specs.map((m) => {
                    const checked = enabled.has(m.key);
                    const available = isAvailable(m, vertical, overrideEnabled);
                    // Disable the row only if it's unavailable AND not
                    // currently enabled. If a previous SA flipped on a
                    // cross-vertical module then turned override off,
                    // the row stays interactive so they can switch it
                    // off (cleanup path) without re-enabling override.
                    const disabled = flagsMutation.isPending || (!available && !checked);
                    return (
                      <li
                        key={m.key}
                        className={`border-border/60 flex items-start gap-3 rounded-md border px-3 py-2.5 ${
                          !available && !checked ? "opacity-60" : ""
                        }`}
                      >
                        <Switch
                          checked={checked}
                          onCheckedChange={() => toggleModule(m, checked)}
                          disabled={disabled}
                          aria-label={`Enable ${m.name}`}
                        />
                        <div className="grid gap-0.5">
                          <span className="text-sm font-medium">
                            {m.name}
                            {!available && !checked && (
                              <Badge
                                variant="outline"
                                className="ml-2 align-middle text-[10px] uppercase"
                              >
                                Cross-vertical
                              </Badge>
                            )}
                          </span>
                          <span className="text-muted-foreground text-xs">{m.description}</span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })
        )}

        {flagsMutation.isPending && (
          <p className="text-muted-foreground inline-flex items-center gap-1.5 text-xs">
            <Loader2 className="size-3 animate-spin" aria-hidden="true" />
            Saving…
          </p>
        )}
        {error && (
          <p
            role="alert"
            className="text-destructive border-destructive/50 bg-destructive/5 rounded-md border px-3 py-2 text-xs"
          >
            {error}
          </p>
        )}
      </CardContent>

      {/* Cross-vertical confirm prompt (rendered inline rather than a
          full modal — simpler; the action is reversible and low-risk). */}
      {pendingCrossVertical && (
        <CardContent className="grid gap-2 border-t border-amber-500/40 bg-amber-500/5 pt-4">
          <div className="flex items-start gap-2">
            <AlertTriangle
              className="mt-0.5 size-4 text-amber-600 dark:text-amber-400"
              aria-hidden="true"
            />
            <div className="grid gap-0.5">
              <span className="text-sm font-medium">
                Enable {pendingCrossVertical.name} on a {vertical.replace("_", " ")} tenant?
              </span>
              <span className="text-muted-foreground text-xs">
                This module isn't part of the standard {vertical.replace("_", " ")} catalog. Turning
                it on will also flip the cross-vertical override flag for this tenant. You can turn
                both off later from this card.
              </span>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setPendingCrossVertical(null)}
              disabled={flagsMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={confirmCrossVerticalEnable}
              disabled={flagsMutation.isPending}
            >
              Enable + flip override
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// ── Feature-flags card ───────────────────────────────────────

function FeatureFlagsCard({
  tenant,
  qc,
}: {
  tenant: TenantDetail;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const [error, setError] = React.useState<string | null>(null);
  const [pendingKey, setPendingKey] = React.useState<keyof PatchFeatureFlagsBody | null>(null);

  // Phase H5d — single mutation surface for both forms_editable_by_tenant
  // and assessments_authoring_by_tenant. `pendingKey` lets us scope the
  // "Saving…" spinner + the disabled state to whichever toggle the SA
  // is flipping right now, instead of locking the entire card.
  const flagsMutation = useMutation<TenantDetail, ApiError, PatchFeatureFlagsBody>({
    mutationFn: (body) =>
      api.patch<TenantDetail>(`/super-admin/tenants/${tenant.slug}/feature-flags`, body),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ["super-admin", "tenants"] });
    },
    onError: (e) => setError(e.message ?? "Failed to update feature flag."),
    onSettled: () => setPendingKey(null),
  });

  // H5d back-compat: missing field → treat as enabled (matches BE default).
  const assessmentsAuthoring = tenant.feature_flags.assessments_authoring_by_tenant ?? true;
  const learnerFormEditable = tenant.feature_flags.learner_form_editable_by_tenant ?? false;
  const teamFormEditable = tenant.feature_flags.team_form_editable_by_tenant ?? false;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tenant capabilities</CardTitle>
        <CardDescription>
          Decide which authoring surfaces the tenant admin can use in their own portal. The SA can
          always author on the tenant's behalf from this portal.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <label className="flex items-start gap-3">
          <Switch
            checked={tenant.feature_flags.forms_editable_by_tenant}
            onCheckedChange={(next) => {
              setPendingKey("forms_editable_by_tenant");
              flagsMutation.mutate({ forms_editable_by_tenant: next });
            }}
            disabled={flagsMutation.isPending}
            aria-label="Allow tenant admin to edit forms"
          />
          <div className="grid gap-1">
            <span className="text-sm font-medium">Allow tenant admin to edit forms</span>
            <span className="text-muted-foreground text-xs">
              When OFF, the institution admin's Forms editor is hidden and their{" "}
              <code>MANAGE_FORMS</code> API requests return 403. They can still RENDER forms in
              admissions / enrollment flows. You (super admin) can always edit forms via the SA
              portal.
            </span>
            {pendingKey === "forms_editable_by_tenant" && flagsMutation.isPending && (
              <span className="text-muted-foreground inline-flex items-center gap-1.5 text-xs">
                <Loader2 className="size-3 animate-spin" aria-hidden="true" />
                Saving…
              </span>
            )}
          </div>
        </label>

        <label className="flex items-start gap-3">
          <Switch
            checked={assessmentsAuthoring}
            onCheckedChange={(next) => {
              setPendingKey("assessments_authoring_by_tenant");
              flagsMutation.mutate({ assessments_authoring_by_tenant: next });
            }}
            disabled={flagsMutation.isPending}
            aria-label="Allow tenant admin to author assessments"
          />
          <div className="grid gap-1">
            <span className="text-sm font-medium">Allow tenant admin to author assessments</span>
            <span className="text-muted-foreground text-xs">
              When OFF, the institution admin's question bank, topics and assessment templates
              become read-only and any write-side API call returns 403 with{" "}
              <code>FEATURE_FLAG_DISABLED</code>. Existing assessments remain runnable in admissions
              flows. The SA can still author on the tenant's behalf from this portal.
            </span>
            {pendingKey === "assessments_authoring_by_tenant" && flagsMutation.isPending && (
              <span className="text-muted-foreground inline-flex items-center gap-1.5 text-xs">
                <Loader2 className="size-3 animate-spin" aria-hidden="true" />
                Saving…
              </span>
            )}
          </div>
        </label>

        <label className="flex items-start gap-3">
          <Switch
            checked={learnerFormEditable}
            onCheckedChange={(next) => {
              setPendingKey("learner_form_editable_by_tenant");
              flagsMutation.mutate({ learner_form_editable_by_tenant: next });
            }}
            disabled={flagsMutation.isPending}
            aria-label="Allow tenant admin to edit the learner form"
          />
          <div className="grid gap-1">
            <span className="text-sm font-medium">Allow tenant admin to edit the learner form</span>
            <span className="text-muted-foreground text-xs">
              When OFF, the institution sees its learner form read-only in Settings and{" "}
              <code>PUT /me/learner-form</code> returns 403 with <code>FEATURE_FLAG_DISABLED</code>.
              You can always edit it from the Forms tab here.
            </span>
            {pendingKey === "learner_form_editable_by_tenant" && flagsMutation.isPending && (
              <span className="text-muted-foreground inline-flex items-center gap-1.5 text-xs">
                <Loader2 className="size-3 animate-spin" aria-hidden="true" />
                Saving…
              </span>
            )}
          </div>
        </label>

        <label className="flex items-start gap-3">
          <Switch
            checked={teamFormEditable}
            onCheckedChange={(next) => {
              setPendingKey("team_form_editable_by_tenant");
              flagsMutation.mutate({ team_form_editable_by_tenant: next });
            }}
            disabled={flagsMutation.isPending}
            aria-label="Allow tenant admin to edit the team form"
          />
          <div className="grid gap-1">
            <span className="text-sm font-medium">Allow tenant admin to edit the team form</span>
            <span className="text-muted-foreground text-xs">
              When OFF, the institution sees its team invite form read-only in Settings and{" "}
              <code>PUT /me/team-form</code> returns 403 with <code>FEATURE_FLAG_DISABLED</code>.
              You can always edit it from the Forms tab here.
            </span>
            {pendingKey === "team_form_editable_by_tenant" && flagsMutation.isPending && (
              <span className="text-muted-foreground inline-flex items-center gap-1.5 text-xs">
                <Loader2 className="size-3 animate-spin" aria-hidden="true" />
                Saving…
              </span>
            )}
          </div>
        </label>

        {error && (
          <p
            role="alert"
            className="text-destructive border-destructive/50 bg-destructive/5 rounded-md border px-3 py-2 text-xs"
          >
            {error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ── Forms card ────────────────────────────────────────────────────────

function FormsCard({
  tenant,
  qc,
}: {
  tenant: TenantDetail;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const formsQuery = useQuery<FormListResponse>({
    queryKey: ["super-admin", "tenant-forms", tenant.slug],
    queryFn: () => api.get<FormListResponse>(`/super-admin/tenants/${tenant.slug}/forms`),
  });

  const templatesQuery = useQuery<FormTemplateListResponse>({
    queryKey: ["onboarding", "form-templates", tenant.type],
    queryFn: () =>
      api.get<FormTemplateListResponse>(
        `/onboarding/form-templates?vertical=${encodeURIComponent(tenant.type)}`,
      ),
    staleTime: 5 * 60 * 1000,
  });

  const cloneMutation = useMutation<CloneTemplatesResponse, ApiError, string[]>({
    mutationFn: (keys) =>
      api.post<CloneTemplatesResponse>(
        `/super-admin/tenants/${tenant.slug}/forms/clone-templates`,
        { template_keys: keys },
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["super-admin", "tenant-forms", tenant.slug],
      });
    },
  });

  const [picking, setPicking] = React.useState<Set<string>>(new Set());
  const togglePick = (key: string) => {
    setPicking((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const cloned = formsQuery.data?.items ?? [];
  const clonedSlugs = new Set(cloned.map((f) => f.slug));
  const availableTemplates =
    templatesQuery.data?.items.filter((t) => !clonedSlugs.has(t.slug)) ?? [];

  return (
    <div className="grid gap-4">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <div className="grid gap-1.5">
            <CardTitle>Forms ({cloned.length})</CardTitle>
            <CardDescription>
              Every form attached to this tenant — both ones cloned from canonical templates and
              custom ones built from scratch. The tenant admin can{" "}
              {tenant.feature_flags.forms_editable_by_tenant ? "edit" : "view"} these depending on
              the toggle in Settings.
            </CardDescription>
          </div>
          <NewFormDialog tenantSlug={tenant.slug} existingSlugs={clonedSlugs} />
        </CardHeader>
        <CardContent>
          {formsQuery.isLoading ? (
            <p className="text-muted-foreground inline-flex items-center gap-2 text-sm">
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              Loading forms…
            </p>
          ) : cloned.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              This tenant has no forms yet. Either clone a canonical template below or click{" "}
              <strong>New custom form</strong> above to build one from scratch.
            </p>
          ) : (
            <ul className="grid gap-2">
              {cloned.map((f) => (
                <li
                  key={f.id}
                  className="border-border/60 flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2.5"
                >
                  <div className="grid min-w-0 gap-0.5">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="text-sm font-medium">{f.name}</span>
                      <code className="text-muted-foreground text-[11px]">{f.slug}</code>
                      <Badge variant={f.requires_auth ? "default" : "success"}>
                        {f.requires_auth ? "internal" : "public"}
                      </Badge>
                    </div>
                    <span className="text-muted-foreground text-xs">
                      {f.fields.length} fields · v{f.version} · updated {formatDate(f.updated_at)}
                    </span>
                  </div>
                  {/* Phase B2.2: Edit-fields entry-point. Opens the
                      tenant-scoped form editor (PATCH spawns a new
                      active version, audit captures the SA actor). */}
                  <Button variant="outline" size="sm" asChild>
                    <Link
                      to={`/tenants/${tenant.slug}/forms/${f.slug}/edit`}
                      className="inline-flex items-center gap-1.5"
                    >
                      <Pencil className="size-3.5" aria-hidden="true" />
                      Edit fields
                    </Link>
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Clone canonical templates</CardTitle>
          <CardDescription>
            Canonical templates are EduViaOS-curated forms (e.g. school admission enquiry, coaching
            demo-class enquiry). Cloning <strong>deep-copies</strong> the template into this tenant
            as a fresh editable form — the tenant can then version it freely without affecting the
            source-of-truth template. Idempotent — re-running with already-cloned keys is a no-op.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3">
          {templatesQuery.isLoading ? (
            <p className="text-muted-foreground inline-flex items-center gap-2 text-sm">
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              Loading templates…
            </p>
          ) : availableTemplates.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              All canonical templates for this vertical are already cloned.
            </p>
          ) : (
            <ul className="grid gap-1.5">
              {availableTemplates.map((t) => {
                const checked = picking.has(t.key);
                return (
                  <li key={t.key} className="flex items-start gap-2">
                    <input
                      id={`tdtl-${t.key}`}
                      type="checkbox"
                      className="mt-0.5"
                      checked={checked}
                      onChange={() => togglePick(t.key)}
                    />
                    <label
                      htmlFor={`tdtl-${t.key}`}
                      className="grid cursor-pointer gap-0.5 text-sm"
                    >
                      <span className="font-medium">{t.name}</span>
                      <span className="text-muted-foreground text-xs">{t.description}</span>
                      <span className="text-muted-foreground text-[10px] uppercase tracking-wide">
                        {t.field_count} fields · {t.requires_auth ? "internal" : "public"}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <span className="text-muted-foreground text-xs">
              {picking.size === 0
                ? "Pick at least one template to clone."
                : `${picking.size} selected.`}
            </span>
            <Button
              type="button"
              size="sm"
              disabled={picking.size === 0 || cloneMutation.isPending}
              onClick={() => {
                cloneMutation.mutate(Array.from(picking), {
                  onSuccess: () => setPicking(new Set()),
                });
              }}
            >
              {cloneMutation.isPending ? "Cloning…" : "Clone selected"}
            </Button>
          </div>
          {cloneMutation.isError && (
            <p
              role="alert"
              className="text-destructive border-destructive/50 bg-destructive/5 rounded-md border px-3 py-2 text-xs"
            >
              {cloneMutation.error?.message ?? "Failed to clone templates. Try again."}
            </p>
          )}
          {cloneMutation.isSuccess && cloneMutation.data && (
            <p className="text-success border-success/40 bg-success/5 rounded-md border px-3 py-2 text-xs">
              Cloned {cloneMutation.data.cloned.length} template(s)
              {cloneMutation.data.skipped.length > 0
                ? ` · skipped ${cloneMutation.data.skipped.length} already-present`
                : ""}
              .
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
